import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Loader2, X } from 'lucide-react';
import { pdfPageStepFromArrowKey } from '../../utils/pdfPageArrowKeys';
import './VendorQuotePdfModal.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export interface VendorQuotePdfModalProps {
  url: string;
  title: string;
  onClose: () => void;
}

const VendorQuotePdfModal: React.FC<VendorQuotePdfModalProps> = ({ url, title, onClose }) => {
  const canvasWrapRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageWidth, setPageWidth] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const goPrevPage = useCallback(() => setPageNumber(page => Math.max(1, page - 1)), []);
  const goNextPage = useCallback(
    () => setPageNumber(page => Math.min(numPages || page, page + 1)),
    [numPages],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const step = pdfPageStepFromArrowKey(event);
      if (step) {
        event.preventDefault();
        if (step < 0) goPrevPage();
        else goNextPage();
        return;
      }
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [goNextPage, goPrevPage, onClose]);

  useEffect(() => {
    const el = canvasWrapRef.current;
    if (!el) return;
    const updateWidth = () => {
      const next = Math.min(el.clientWidth - 32, 920);
      setPageWidth(next > 0 ? next : 720);
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="vp-quote-pdf-overlay" role="presentation" onClick={onClose}>
      <div
        className="vp-quote-pdf-shell"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={event => event.stopPropagation()}
      >
        <header className="vp-quote-pdf-header">
          <h2 className="vp-quote-pdf-title">{title}</h2>
          <button type="button" className="vp-quote-pdf-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="vp-quote-pdf-main" ref={canvasWrapRef}>
          {loading && (
            <div className="vp-quote-pdf-loading">
              <Loader2 size={22} className="vp-quote-pdf-spinner" aria-hidden />
              Loading PDF…
            </div>
          )}
          {error && <div className="vp-quote-pdf-error">{error}</div>}
          <Document
            file={url}
            onLoadSuccess={pdf => {
              setNumPages(pdf.numPages);
              setLoading(false);
            }}
            onLoadError={err => {
              setError(err.message || 'Could not load PDF.');
              setLoading(false);
            }}
            loading={null}
          >
            {pageWidth != null && (
              <Page
                pageNumber={pageNumber}
                width={pageWidth}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            )}
          </Document>
        </div>

        {numPages > 1 && (
          <footer className="vp-quote-pdf-footer">
            <button
              type="button"
              disabled={pageNumber <= 1}
              onClick={goPrevPage}
            >
              Previous
            </button>
            <span>
              Page {pageNumber} of {numPages}
            </span>
            <button
              type="button"
              disabled={pageNumber >= numPages}
              onClick={goNextPage}
            >
              Next
            </button>
          </footer>
        )}
      </div>
    </div>
  );
};

export default VendorQuotePdfModal;
